/**
 * Pure H · Product Master · Workspace Tabs
 * =========================================
 * Encapsulates the 6 new product-workspace tabs so the main layout
 * file stays readable. Each tab is a self-contained React component
 * that owns its own fetch + refresh cycle.
 *
 *   1. ReadinessTab  — 0-100 score + breakdown + missing[] action list
 *   2. TimelineTab   — chronological events (created / updated / asset.added / approval.*)
 *   3. AssetsTab     — image gallery (upload + preview)
 *   4. DocumentsTab  — PDFs / DOCX / XLSX (SDS · IFRA · packaging · etc.)
 *   5. RevisionsTab  — audit-derived diff log
 *   6. ApprovalTab   — workflow drawer w/ state machine actions
 */
import { useCallback, useEffect, useState } from "react";
import {
  Activity, Clock, Image as ImageIcon, FileText, History,
  ClipboardCheck, RefreshCcw, Upload, Trash2, Plus, Send,
  CheckCircle2, XCircle, AlertTriangle, Sparkles, ExternalLink,
} from "lucide-react";
import { Card, Button, EmptyState, ScoreDial, SectionHeader } from "../../components/lib";
import {
  productMaster,
  PMReadiness, PMEvent, PMAsset, PMDocument, PMRevision, PMApproval,
} from "../../services/api";

// ── Shared helpers ───────────────────────────────────────────────
function fmt(iso?: string) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function humanBytes(n?: number): string {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

async function fileToBase64(f: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(f);
  });
}

const KIND_COLORS: Record<string, string> = {
  created: "var(--ph-success)",
  updated: "var(--ph-ai)",
  archived: "var(--ph-danger)",
  "asset.added": "var(--ph-brand-primary)",
  "asset.removed": "var(--ph-ink-muted)",
  "doc.added": "var(--ph-brand-primary)",
  "doc.removed": "var(--ph-ink-muted)",
  "approval.submitted": "var(--ph-warning)",
  "approval.in_review": "var(--ph-warning)",
  "approval.approved": "var(--ph-success)",
  "approval.published": "var(--ph-success)",
  "approval.rejected":  "var(--ph-danger)",
  "approval.draft":     "var(--ph-ink-muted)",
  manual: "var(--ph-ai)",
};

// ══════════════════════════════════════════════════════════════
// 1) READINESS TAB
// ══════════════════════════════════════════════════════════════
export function ReadinessTab({ ventureId, productId, initial, onRecomputed }: {
  ventureId: string; productId: string;
  initial?: PMReadiness | null;
  onRecomputed?: (r: PMReadiness) => void;
}) {
  const [r, setR] = useState<PMReadiness | null>(initial ?? null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setR(initial ?? null); }, [initial, productId]);

  const recompute = async () => {
    setBusy(true);
    try {
      const next = await productMaster.recomputeReadiness(ventureId, productId);
      setR(next); onRecomputed?.(next);
    } finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="readiness-tab">
      <Card>
        <SectionHeader
          eyebrow="Product Readiness"
          title="Overall score"
          action={<Button variant="secondary" onClick={recompute} disabled={busy} testId="recompute-readiness">
            <RefreshCcw size={14} /> Recompute
          </Button>}
        />
        {!r ? <EmptyState title="No score yet" hint="Click Recompute to calculate." /> : (
          <div>
            <div className="flex items-center gap-4">
              <ScoreDial score={r.score} />
              <div>
                <div className="text-3xl font-bold text-slate-900" data-testid="readiness-score">
                  {r.score}<span className="text-lg text-slate-500">/100</span>
                </div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Last computed · {fmt(r.computedAt)}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-3 mt-3 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
              {Object.entries(r.breakdown).map(([k, v]) => (
                <div key={k} data-testid={`breakdown-${k}`}>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                    {k.replace(/([A-Z])/g, " $1").trim()}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--ph-neutral-tint)" }}>
                      <div className="h-full" style={{ width: `${v}%`, background: "var(--ph-brand-primary)" }} />
                    </div>
                    <div className="text-sm font-semibold text-slate-900 w-10 text-right">{v}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader
          eyebrow="Action list"
          title={r ? `${r.missing.length} missing field${r.missing.length === 1 ? "" : "s"}` : "Missing fields"}
        />
        {!r || r.missing.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-600">
            <CheckCircle2 size={16} /> All fields populated. Product is complete.
          </div>
        ) : (
          <ul className="space-y-1.5 max-h-[420px] overflow-y-auto" data-testid="missing-list">
            {r.missing.map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`missing-${i}`}>
                <AlertTriangle size={12} className="mt-0.5 text-amber-500 shrink-0" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 2) TIMELINE TAB
// ══════════════════════════════════════════════════════════════
export function TimelineTab({ ventureId, productId }: { ventureId: string; productId: string }) {
  const [events, setEvents] = useState<PMEvent[]>([]);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try { setEvents(await productMaster.timeline(ventureId, productId)); }
    finally { setBusy(false); }
  }, [ventureId, productId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!title.trim()) return;
    await productMaster.appendTimeline(ventureId, productId, { title: title.trim(), body: body.trim() });
    setTitle(""); setBody(""); setShowForm(false);
    await load();
  };

  return (
    <Card testId="timeline-tab">
      <SectionHeader
        eyebrow="Product timeline"
        title="History of every change"
        action={<Button variant="primary" onClick={() => setShowForm((s) => !s)} testId="add-note-btn">
          <Plus size={14} /> Add note
        </Button>}
      />

      {showForm && (
        <div className="p-3 rounded mb-4" data-testid="timeline-form"
          style={{ background: "var(--ph-surface-alt)", border: "1px dashed var(--ph-border-strong)" }}>
          <div className="grid gap-2">
            <input placeholder="Title" data-testid="note-title"
              value={title} onChange={(e) => setTitle(e.target.value)}
              className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} />
            <textarea placeholder="Body (optional)" rows={2} data-testid="note-body"
              value={body} onChange={(e) => setBody(e.target.value)}
              className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} />
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setShowForm(false); setTitle(""); setBody(""); }} testId="note-cancel">Cancel</Button>
            <Button variant="primary" onClick={addNote} testId="note-save" disabled={!title.trim()}>
              <Plus size={14} /> Add
            </Button>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <EmptyState title={busy ? "Loading…" : "No events yet"} hint="Events are added automatically when you edit, upload, or move the workflow." />
      ) : (
        <ol className="relative pl-6 space-y-4" data-testid="timeline-list">
          {events.map((ev) => (
            <li key={ev.id} className="relative" data-testid={`event-${ev.id}`}>
              <span className="absolute -left-6 top-1.5 w-3 h-3 rounded-full" style={{ background: KIND_COLORS[ev.kind] || "var(--ph-ink-muted)" }} />
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                {fmt(ev.at)} · {ev.kind}
              </div>
              <div className="text-sm font-semibold text-slate-900 mt-0.5" style={{ fontFamily: "var(--ph-display-font)" }}>
                {ev.title}
              </div>
              {ev.body && <div className="text-sm text-slate-700 mt-0.5">{ev.body}</div>}
              {ev.actorEmail && <div className="text-[11px] text-slate-500 mt-0.5">by {ev.actorEmail}</div>}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════
// 3) ASSETS TAB
// ══════════════════════════════════════════════════════════════
const ASSET_ROLES = ["hero", "packshot", "lifestyle", "moodboard", "gallery", "detail"];

export function AssetsTab({ ventureId, productId, onChanged }: {
  ventureId: string; productId: string; onChanged?: () => void;
}) {
  const [assets, setAssets] = useState<PMAsset[]>([]);
  const [uploading, setUploading] = useState(false);
  const [role, setRole] = useState("hero");
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setAssets(await productMaster.listAssets(ventureId, productId));
  }, [ventureId, productId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file: File) => {
    setErr(null); setUploading(true);
    try {
      const b64 = await fileToBase64(file);
      await productMaster.createAsset(ventureId, productId, {
        name: file.name, mime: file.type || "image/jpeg", role, dataBase64: b64,
      });
      await load(); onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this asset?")) return;
    await productMaster.deleteAsset(ventureId, productId, id);
    await load(); onChanged?.();
  };

  return (
    <Card testId="assets-tab">
      <SectionHeader eyebrow="Product assets" title="Images, packshots, moodboards"
        action={
          <div className="flex items-center gap-2">
            <select value={role} onChange={(e) => setRole(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded"
              style={{ borderColor: "var(--ph-border-strong)" }}
              data-testid="asset-role-select">
              {ASSET_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <label className="inline-flex items-center gap-1 cursor-pointer px-3 py-1.5 rounded text-xs font-semibold text-white"
              style={{ background: "var(--ph-brand-primary)" }} data-testid="asset-upload-label">
              <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
              <input type="file" accept="image/*" hidden disabled={uploading} data-testid="asset-upload-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
            </label>
          </div>
        }
      />
      {err && <div className="mb-3 text-xs text-red-600 flex items-center gap-1"><AlertTriangle size={12} /> {err}</div>}
      {assets.length === 0 ? (
        <EmptyState title="No assets yet" hint="Upload hero images, packshots, and moodboards to enable Shopify readiness." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="asset-grid">
          {assets.map((a) => (
            <div key={a.id} className="rounded border overflow-hidden group relative"
              style={{ borderColor: "var(--ph-border-subtle)" }}
              data-testid={`asset-${a.id}`}>
              <div className="aspect-square flex items-center justify-center" style={{ background: "var(--ph-surface-alt)" }}>
                {a.url ? <img src={a.url} alt={a.altText || a.name} className="w-full h-full object-cover" />
                  : <ImageIcon size={30} className="text-slate-400" />}
              </div>
              <div className="p-2 text-[11px]">
                <div className="font-semibold text-slate-800 truncate" title={a.name}>{a.name}</div>
                <div className="text-slate-500 flex items-center justify-between">
                  <span className="uppercase tracking-wider">{a.role}</span>
                  <span>{humanBytes(a.sizeBytes)}</span>
                </div>
              </div>
              <button
                onClick={() => remove(a.id)}
                className="absolute top-1 right-1 p-1 rounded bg-white/95 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`asset-delete-${a.id}`}>
                <Trash2 size={12} className="text-red-600" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════
// 4) DOCUMENTS TAB
// ══════════════════════════════════════════════════════════════
const DOC_CATEGORIES = ["sds", "ifra", "packaging", "regulatory", "marketing_brief", "supplier_cert", "test_report", "other"];

export function DocumentsTab({ ventureId, productId }: { ventureId: string; productId: string }) {
  const [docs, setDocs] = useState<PMDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("sds");
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setDocs(await productMaster.listDocuments(ventureId, productId));
  }, [ventureId, productId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file: File) => {
    setErr(null); setUploading(true);
    try {
      const b64 = await fileToBase64(file);
      await productMaster.createDocument(ventureId, productId, {
        name: file.name, mime: file.type || "application/octet-stream", category, dataBase64: b64,
      });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this document?")) return;
    await productMaster.deleteDocument(ventureId, productId, id);
    await load();
  };

  return (
    <Card testId="documents-tab">
      <SectionHeader eyebrow="Product documents" title="SDS · IFRA · Packaging · Briefs"
        action={
          <div className="flex items-center gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded"
              style={{ borderColor: "var(--ph-border-strong)" }}
              data-testid="doc-category-select">
              {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
            </select>
            <label className="inline-flex items-center gap-1 cursor-pointer px-3 py-1.5 rounded text-xs font-semibold text-white"
              style={{ background: "var(--ph-brand-primary)" }} data-testid="doc-upload-label">
              <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" hidden disabled={uploading} data-testid="doc-upload-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
            </label>
          </div>
        }
      />
      {err && <div className="mb-3 text-xs text-red-600 flex items-center gap-1"><AlertTriangle size={12} /> {err}</div>}
      {docs.length === 0 ? (
        <EmptyState title="No documents yet" hint="Attach Safety Data Sheets, IFRA certs, packaging specs and marketing briefs." />
      ) : (
        <div className="overflow-x-auto -mx-2 px-2">
          <table className="min-w-full text-sm" data-testid="doc-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <th className="py-2 pr-3 font-semibold">Category</th>
                <th className="py-2 pr-3 font-semibold">Name</th>
                <th className="py-2 pr-3 font-semibold">Size</th>
                <th className="py-2 pr-3 font-semibold">Uploaded</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-t hover:bg-slate-50" style={{ borderColor: "var(--ph-border-subtle)" }}
                    data-testid={`doc-row-${d.id}`}>
                  <td className="py-2 pr-3">
                    <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded"
                      style={{ background: "var(--ph-neutral-tint)", color: "var(--ph-ink-muted)" }}>
                      {d.category.replace("_", " ")}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-slate-800 font-medium">{d.name}</td>
                  <td className="py-2 pr-3 text-slate-600">{humanBytes(d.sizeBytes)}</td>
                  <td className="py-2 pr-3 text-slate-500 text-xs">{fmt(d.uploadedAt)}</td>
                  <td className="py-2 pr-3">
                    <button onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600" data-testid={`doc-delete-${d.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════
// 5) REVISIONS TAB
// ══════════════════════════════════════════════════════════════
export function RevisionsTab({ ventureId, productId }: { ventureId: string; productId: string }) {
  const [revs, setRevs] = useState<PMRevision[]>([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    try { setRevs(await productMaster.revisions(ventureId, productId)); }
    finally { setBusy(false); }
  }, [ventureId, productId]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card testId="revisions-tab">
      <SectionHeader
        eyebrow="Revision history"
        title="Every change, with diff"
        action={<Button variant="secondary" onClick={load} disabled={busy} testId="reload-revisions"><RefreshCcw size={14} /> Reload</Button>}
      />
      {revs.length === 0 ? (
        <EmptyState title="No revisions yet" hint="Every PATCH, approval and archive is logged automatically." />
      ) : (
        <ul className="space-y-2" data-testid="revisions-list">
          {revs.map((r) => {
            const isOpen = expanded === r.id;
            const isUpdate = r.operation === "product.update";
            return (
              <li key={r.id} className="rounded border" style={{ borderColor: "var(--ph-border-subtle)" }}
                  data-testid={`revision-${r.id}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                  className="w-full text-left p-3 flex items-start justify-between gap-3 hover:bg-slate-50"
                  data-testid={`revision-toggle-${r.id}`}>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                      {fmt(r.at)} · v{r.payload?.version ?? "—"}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5" style={{ fontFamily: "var(--ph-display-font)" }}>
                      {r.operation.replace("product.", "")}
                    </div>
                    {r.payload?.fields && r.payload.fields.length > 0 && (
                      <div className="text-xs text-slate-600 mt-0.5">
                        Changed: {r.payload.fields.join(", ")}
                      </div>
                    )}
                    {r.payload?.from && r.payload?.to && (
                      <div className="text-xs text-slate-600 mt-0.5">
                        {r.payload.from} → {r.payload.to}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-500 mt-1">{r.actorEmail || "system"}</div>
                  </div>
                  <History size={14} className={`shrink-0 mt-1 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && isUpdate && r.payload?.diff && Object.keys(r.payload.diff).length > 0 && (
                  <div className="px-3 pb-3 text-xs" data-testid={`revision-diff-${r.id}`}>
                    <div className="border-t pt-2 mt-1 space-y-2" style={{ borderColor: "var(--ph-border-subtle)" }}>
                      {Object.entries(r.payload.diff).map(([field, delta]: [string, any]) => (
                        <div key={field}>
                          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-0.5">{field}</div>
                          {delta && typeof delta === "object" && "before" in delta ? (
                            <div className="grid grid-cols-2 gap-2">
                              <div className="p-1.5 rounded" style={{ background: "var(--ph-danger-tint)" }}>
                                <div className="text-[9px] text-red-600 font-bold">BEFORE</div>
                                <div className="text-slate-700 break-words">{JSON.stringify(delta.before) || "—"}</div>
                              </div>
                              <div className="p-1.5 rounded" style={{ background: "var(--ph-success-tint)" }}>
                                <div className="text-[9px] text-emerald-700 font-bold">AFTER</div>
                                <div className="text-slate-700 break-words">{JSON.stringify(delta.after) || "—"}</div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-slate-600">Sub-fields: {Object.keys(delta || {}).join(", ")}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════
// 6) APPROVAL TAB
// ══════════════════════════════════════════════════════════════
const STATE_TONES: Record<string, "neutral" | "info" | "warning" | "success" | "danger"> = {
  draft: "neutral", submitted: "warning", in_review: "warning",
  approved: "success", published: "success", rejected: "danger",
};
const STATE_LABEL: Record<string, string> = {
  draft: "Draft", submitted: "Submitted", in_review: "In Review",
  approved: "Approved", published: "Published", rejected: "Rejected",
};

export function ApprovalTab({
  ventureId, productId, approval, onChanged,
}: {
  ventureId: string; productId: string;
  approval?: PMApproval | null;
  onChanged?: (a: PMApproval) => void;
}) {
  const [meta, setMeta] = useState<{ states: string[]; transitions: Record<string, string[]> } | null>(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [local, setLocal] = useState<PMApproval | null>(approval || null);
  useEffect(() => { setLocal(approval || null); }, [approval, productId]);
  useEffect(() => { productMaster.approvalMeta().then(setMeta).catch(() => setMeta(null)); }, []);

  const state = local?.state || "draft";
  const nextStates = meta?.transitions?.[state] || [];

  const transition = async (toState: string) => {
    setBusy(toState); setErr(null);
    try {
      const next = await productMaster.approvalTransition(ventureId, productId, toState, comment || undefined);
      setLocal(next); onChanged?.(next); setComment("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Transition failed.");
    } finally {
      setBusy(null);
    }
  };

  const tone = STATE_TONES[state] || "neutral";
  const palette = {
    neutral: { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)" },
    warning: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)" },
    info:    { bg: "rgba(79,93,184,.10)",    fg: "var(--ph-ai)" },
    success: { bg: "var(--ph-success-tint)", fg: "var(--ph-success)" },
    danger:  { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)" },
  }[tone];

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="approval-tab">
      <Card>
        <SectionHeader eyebrow="Approval workflow" title="Current state" />
        <div className="flex items-center gap-3 mb-4">
          <span
            className="inline-flex items-center px-3 py-1 text-sm font-bold rounded"
            style={{ background: palette.bg, color: palette.fg, letterSpacing: "0.10em", textTransform: "uppercase" }}
            data-testid="approval-state-pill">
            {STATE_LABEL[state]}
          </span>
          {local?.approvedAt && <span className="text-[11px] text-slate-500">approved · {fmt(local.approvedAt)}</span>}
          {local?.publishedAt && <span className="text-[11px] text-slate-500">published · {fmt(local.publishedAt)}</span>}
          {local?.rejectedReason && <span className="text-[11px] text-red-600">reason: {local.rejectedReason}</span>}
        </div>

        <div className="grid gap-2 mb-3">
          <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Comment (optional)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            placeholder="Notes for the reviewer / approver / rejector…"
            className="px-3 py-2 text-sm border rounded"
            style={{ borderColor: "var(--ph-border-strong)" }}
            data-testid="approval-comment"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {nextStates.length === 0 && <div className="text-xs text-slate-500">No further transitions from this state.</div>}
          {nextStates.map((s) => (
            <Button
              key={s}
              variant={s === "rejected" ? "ghost" : (s === "approved" || s === "published" ? "primary" : "secondary")}
              onClick={() => transition(s)}
              disabled={!!busy}
              testId={`transition-${s}`}
            >
              {s === "rejected" ? <XCircle size={14} /> : s === "approved" || s === "published" ? <CheckCircle2 size={14} /> : <Send size={14} />}
              {busy === s ? "…" : `→ ${STATE_LABEL[s]}`}
            </Button>
          ))}
        </div>
        {err && <div className="mt-3 text-xs text-red-600 flex items-center gap-1" data-testid="approval-error"><AlertTriangle size={12} /> {err}</div>}
      </Card>

      <Card>
        <SectionHeader eyebrow="History" title={`${local?.history?.length ?? 0} transition${local?.history?.length === 1 ? "" : "s"}`} />
        {!local?.history || local.history.length === 0 ? (
          <EmptyState title="No transitions yet" hint="Submit the product to start the workflow." />
        ) : (
          <ol className="space-y-2" data-testid="approval-history">
            {[...local.history].reverse().map((h, i) => (
              <li key={i} className="p-3 rounded border" style={{ borderColor: "var(--ph-border-subtle)" }} data-testid={`history-${i}`}>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{fmt(h.at)}</div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5">
                  {STATE_LABEL[h.from] || h.from} <span className="text-slate-400">→</span> {STATE_LABEL[h.to] || h.to}
                </div>
                {h.actorEmail && <div className="text-[11px] text-slate-500 mt-0.5">by {h.actorEmail}</div>}
                {h.comment && <div className="text-xs text-slate-700 mt-1 italic">&ldquo;{h.comment}&rdquo;</div>}
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
